import React, { useEffect, useState } from 'react';
import { Text, StyleSheet, View, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { MB_Button } from '@/components/general/button';
import { isMobile } from '@/utils/general/device-info';
import { ModalForPopUp } from './ModalForPopUp';
import { MB_ModalAnimationProps } from '@/components/general/modal';

interface MB_PopUpSetDefaultStylesProps {
  /**
   * Style to apply to the container of the pop-up.
   */
  defaultContainerStyle?: StyleProp<ViewStyle>;

  /**
   * Style to apply to the title text.
   */
  defaultTitleStyle?: StyleProp<TextStyle>;

  /**
   * Style to apply to the message text.
   */
  defaultMessageStyle?: StyleProp<TextStyle>;

  /**
   * Style to apply to the top component wrapper.
   */
  defaultTopComponentWrapperStyle?: StyleProp<ViewStyle>;

  /**
   * Style to apply to the middle component wrapper.
   */
  defaultMiddleComponentWrapperStyle?: StyleProp<ViewStyle>;

  /**
   * Style to apply to the primary button.
   */
  defaultPrimaryButtonStyle?: StyleProp<ViewStyle>;

  /**
   * Style to apply to the primary button text.
   */
  defaultPrimaryButtonTextStyle?: StyleProp<TextStyle>;

  /**
   * Style to apply to the secondary button.
   */
  defaultSeconaryButtonStyle?: StyleProp<ViewStyle>;

  /**
   * Style to apply to the secondary button text.
   */
  defaultSecondaryButtonTextStyle?: StyleProp<TextStyle>;

  /**
   * Style to apply to the buttons container.
   */
  defaultButtonsContainerStyleModifier?: StyleProp<ViewStyle>;
}

const MB_PopUpSetDefaultStyles = (props: MB_PopUpSetDefaultStylesProps) => {
  defaultStyles = StyleSheet.create({
    defaultContainerStyle: StyleSheet.flatten(props.defaultContainerStyle),
    defaultTitleStyle: StyleSheet.flatten(props.defaultTitleStyle),
    defaultMessageStyle: StyleSheet.flatten(props.defaultMessageStyle),
    defaultTopComponentWrapperStyle: StyleSheet.flatten(props.defaultTopComponentWrapperStyle),
    defaultMiddleComponentWrapperStyle: StyleSheet.flatten(props.defaultMiddleComponentWrapperStyle),
    defaultPrimaryButtonStyle: StyleSheet.flatten(props.defaultPrimaryButtonStyle),
    defaultPrimaryButtonTextStyle: StyleSheet.flatten(props.defaultPrimaryButtonTextStyle),
    defaultSeconaryButtonStyle: StyleSheet.flatten(props.defaultSeconaryButtonStyle),
    defaultSecondaryButtonTextStyle: StyleSheet.flatten(props.defaultSecondaryButtonTextStyle),
    defaultButtonsContainerStyleModifier: StyleSheet.flatten(props.defaultButtonsContainerStyleModifier),
  });
};

interface MB_PopUpProps extends Omit<ButtonProps, 'setErrorMessage'> {
  /**
   * Whether or not the pop-up is visible.
   */
  isVisible: boolean;

  /**
   * The text to show in the title of the pop-up.
   */
  title?: string;

  /**
   * Style to apply to the title text.
   */
  titleStyle?: StyleProp<TextStyle>;

  /**
   * The text to show in the message of the pop-up.
   */
  message?: string;

  /**
   * Style to apply to the message text.
   */
  messageStyle?: StyleProp<TextStyle>;

  /**
   * Component to show at the top of the pop-up.
   */
  topComponent?: React.ReactNode;

  /**
   * Component to show in the middle of the pop-up.
   */
  middleComponent?: React.ReactNode;

  /**
   * Style to apply to the container of the pop-up.
   */
  containerStyle?: StyleProp<ViewStyle>;
  animationProps?: MB_ModalAnimationProps;
}

interface ButtonProps {
  /**
   * Text to show in the button of the pop-up.
   */
  buttonText?: string;

  /**
   * Action to perform when the button is pressed.
   */
  buttonAction?: () => void | Promise<void>;

  /**
   * Text to show in the secondary button of the pop-up.
   */
  secondaryButtonText?: string;

  /**
   * Action to perform when the secondary button is pressed.
   */
  secondaryButtonAction?: () => void | Promise<void>;

  /**
   * Called when the pop-up is dismissed.
   */
  onDismiss?: () => void;

  /**
   * Style to apply to the secondary button.
   */
  secondaryButtonStyle?: {
    /**
     * Style to apply to the container of the secondary button.
     */
    container?: StyleProp<ViewStyle>;

    /**
     * Style to apply to the text of the secondary button.
     */
    text?: StyleProp<TextStyle>;
  };

  /**
   * Style to apply to the primary button.
   */
  buttonStyle?: {
    /**
     * Style to apply to the container of the primary button.
     */
    container?: StyleProp<ViewStyle>;

    /**
     * Style to apply to the text of the primary button.
     */
    text?: StyleProp<TextStyle>;
  };

  /**
   * Set an error message to show in the pop-up.
   */
  setErrorMessage: (errorMessage: string) => void;
}

const RenderButtons = (props: ButtonProps) => {
  const [isButtonActionInprogress, setIsButtonActionInProgress] = useState(false);
  const [isSecondaryButtonActionInprogress, setIsSecondaryButtonActionInProgress] = useState(false);

  const handleButtonAction = async () => {
    props.setErrorMessage('');

    if (!props.buttonAction) {
      props.onDismiss?.();
      return;
    }

    setIsButtonActionInProgress(true);
    try {
      await props.buttonAction();
      setIsButtonActionInProgress(false);
    } catch (error) {
      const msg = error && typeof error === 'string' ? error : 'Something went wrong. Please try again';
      props.setErrorMessage(msg);
      setIsButtonActionInProgress(false);
    }
  };

  const handleSecondaryButtonAction = async () => {
    props.setErrorMessage('');
    setIsSecondaryButtonActionInProgress(true);

    if (!props.secondaryButtonAction) {
      props.onDismiss?.();
      return;
    }

    try {
      await props.secondaryButtonAction();
      setIsSecondaryButtonActionInProgress(false);
    } catch (error) {
      const msg = error && typeof error === 'string' ? error : 'Something went wrong. Please try again';
      props.setErrorMessage(msg);
      setIsSecondaryButtonActionInProgress(false);
    }
  };

  return (
    <View style={[styles.buttonsContainer, defaultStyles?.defaultButtonsContainerStyleModifier]}>
      {props.secondaryButtonText && (
        <>
          <MB_Button
            style={[defaultStyles?.defaultSeconaryButtonStyle ?? styles.secondaryButtonStyle, props.secondaryButtonStyle?.container]}
            title={props.secondaryButtonText ?? 'cancel'}
            onPress={handleSecondaryButtonAction}
            disabled={isSecondaryButtonActionInprogress || isButtonActionInprogress}
            loading={isSecondaryButtonActionInprogress}
            textStyle={[defaultStyles?.defaultSecondaryButtonTextStyle ?? styles.buttonTextStyle, props.secondaryButtonStyle?.text]}
          />

          <View style={{ width: 32 }} />
        </>
      )}
      <MB_Button
        style={[defaultStyles?.defaultPrimaryButtonStyle ?? styles.primaryButtonStyle, props.buttonStyle?.container]}
        title={props.buttonText ?? 'OK'}
        onPress={handleButtonAction}
        disabled={isButtonActionInprogress || isSecondaryButtonActionInprogress}
        loading={isButtonActionInprogress}
        textStyle={[defaultStyles?.defaultPrimaryButtonTextStyle ?? styles.buttonTextStyle, props.buttonStyle?.text]}
      />
    </View>
  );
};

const MB_PopUpUI = (props: MB_PopUpProps) => {
  const [isVisibleInternal, setIsVisibleInternal] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (props.isVisible) {
      setErrorMessage('');
      setIsVisibleInternal(true);
    }
  }, [props.isVisible]);

  if (!isVisibleInternal) {
    return null;
  }

  return (
    <ModalForPopUp isVisible={props.isVisible} onHide={() => setIsVisibleInternal(false)} animationProps={props.animationProps}>
      <View style={[defaultStyles?.defaultContainerStyle ?? styles.popUpView, props.containerStyle]}>
        {props.topComponent && (
          <View style={defaultStyles?.defaultTopComponentWrapperStyle ?? styles.topComponentWrapperStyle}>
            {typeof props.topComponent === 'function' ? props.topComponent() : props.topComponent}
          </View>
        )}

        <Text style={props.titleStyle ?? defaultStyles?.defaultTitleStyle ?? styles.titleText}>{props.title}</Text>

        {props.middleComponent && (
          <View style={defaultStyles?.defaultMiddleComponentWrapperStyle ?? styles.middleComponentWrapperStyle}>
            {typeof props.middleComponent === 'function' ? props.middleComponent() : props.middleComponent}
          </View>
        )}

        {props.message && <Text style={props.messageStyle ?? defaultStyles?.defaultMessageStyle ?? styles.messageText}>{props.message}</Text>}

        <RenderButtons
          buttonText={props.buttonText}
          buttonAction={props.buttonAction}
          secondaryButtonText={props.secondaryButtonText}
          secondaryButtonAction={props.secondaryButtonAction}
          onDismiss={props.onDismiss}
          setErrorMessage={setErrorMessage}
          buttonStyle={props.buttonStyle}
          secondaryButtonStyle={props.secondaryButtonStyle}
        />

        {errorMessage !== '' && <Text style={styles.errorMessageText}>{errorMessage}</Text>}
      </View>
    </ModalForPopUp>
  );
};

export { MB_PopUpUI, MB_PopUpSetDefaultStyles };
export type { MB_PopUpProps, MB_PopUpSetDefaultStylesProps };

let defaultStyles: MB_PopUpSetDefaultStylesProps | undefined;

const styles = StyleSheet.create({
  popUpView: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    backgroundColor: 'white',
    borderRadius: 8,
  },
  topComponentWrapperStyle: {
    alignItems: 'center',
    marginBottom: isMobile ? 18 : 32,
  },
  middleComponentWrapperStyle: {
    alignItems: 'center',
    marginTop: 18,
  },
  titleText: {
    fontSize: 20,
    color: 'black',
    marginTop: 8,
    textAlign: 'center',
  },
  buttonsContainer: {
    flexDirection: 'row',
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  buttonTextStyle: {
    fontSize: 16,
    fontWeight: '500',
    color: 'white',
    textAlign: 'center',
  },
  primaryButtonStyle: {
    minWidth: 168,
  },
  secondaryButtonStyle: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'white',
    minWidth: 168,
  },
  messageText: {
    fontSize: 16,
    color: 'black',
    marginTop: 8,
    textAlign: 'center',
  },
  errorMessageText: {
    fontWeight: 'normal',
    fontSize: 14,
    textAlign: 'center',
    color: 'red',
    marginTop: 26,
    maxWidth: 386,
  },
});
